/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Button, Flex, Grid, IconLoading } from "@powerledger/ui-component-lib";
import { addMonths, format, min } from "date-fns";
import { Formik, FormikHelpers } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { date } from "yup";

import { Form, FormFieldLabel, LocalDatePicker } from "@/app/components";
import { AppDateFormats, onChangeDateFormats } from "@/app/lib/format-date";

import { TradingSummaryFilterProps } from "./trading-summary-filter.types";

export const TradingSummaryFilter: FC<TradingSummaryFilterProps> = ({
  generateSummary,
  loading,
  defaultStartDate,
  defaultEndDate,
}) => {
  const { t } = useTranslation();
  const handleOnSubmit = async (
    { startDate, endDate }: { startDate: string; endDate: string },
    { resetForm }: FormikHelpers<{ startDate: string; endDate: string }>,
  ) => {
    await generateSummary(startDate, endDate);
    // to reset the `dirty` formik field
    resetForm({ values: { startDate, endDate } });
  };

  return (
    <Flex
      sx={{
        mt: 4,
        mb: 3,
        bg: "secondaryDarker",
        flexWrap: "wrap",
        borderRadius: "8px",
        p: 3,
      }}
    >
      <Box sx={{ flex: 2 }}>
        <Formik
          initialValues={{ startDate: defaultStartDate, endDate: defaultEndDate }}
          onSubmit={handleOnSubmit}
          validationSchema={Yup.object().shape({
            startDate: date().required("Required"),
            endDate: date().required("Required"),
          })}
        >
          {({ handleSubmit, setFieldValue, values }) => (
            <Form onSubmit={handleSubmit}>
              <Grid gap={3} columns={[1, null, 3]}>
                <Form.Item>
                  <FormFieldLabel
                    small
                    name="startDate"
                    label={t("Start Date *")}
                    sx={
                      loading
                        ? {
                            input: {
                              cursor: "not-allowed",
                              opacity: 0.5,
                            },
                          }
                        : {}
                    }
                  >
                    <LocalDatePicker
                      id="startDate"
                      noOffset
                      value={values.startDate}
                      onChange={(value: string) => {
                        setFieldValue("startDate", value || "");
                        let endDate = new Date(values.endDate);
                        const startDate = new Date(value);
                        const now = new Date();
                        if (endDate < startDate) {
                          endDate = min([now, addMonths(startDate, 1)]);
                          setFieldValue(
                            "endDate",
                            format(endDate, onChangeDateFormats[AppDateFormats.UniversalDateFormat]),
                          );
                        }
                      }}
                      disabled={loading}
                      maxDate={new Date()}
                    />
                  </FormFieldLabel>
                </Form.Item>
                <Form.Item>
                  <FormFieldLabel
                    small
                    name="endDate"
                    label={t("End Date *")}
                    sx={
                      loading
                        ? {
                            input: {
                              cursor: "not-allowed",
                              opacity: 0.5,
                            },
                          }
                        : {}
                    }
                  >
                    <LocalDatePicker
                      id="endDate"
                      value={values.endDate}
                      noOffset
                      disabled={loading}
                      onChange={(value: string) => {
                        setFieldValue("endDate", value || "");
                      }}
                      minDate={values.startDate ? new Date(values.startDate) : null}
                      maxDate={new Date()}
                    />
                  </FormFieldLabel>
                </Form.Item>
                <Flex sx={{ justifyContent: "flex-end", alignItems: "center" }}>
                  {loading ? (
                    <IconLoading sx={{ pr: 3 }} />
                  ) : (
                    <Button type="submit" variant="pill">
                      {t("Generate")}
                    </Button>
                  )}
                </Flex>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </Flex>
  );
};
